/**
 * PostHog client-side initialization, consent-gated.
 *
 * PostHog only loads when:
 *   1. The user has granted analytics consent via `wtm_consent` cookie
 *   2. `NEXT_PUBLIC_POSTHOG_KEY` is set in env
 *   3. We're on the client (typeof window !== 'undefined')
 *
 * The lib exposes:
 *   - `posthogClient()` — lazy-getter that returns the initialized
 *     posthog-js instance, or `null` if any of the above conditions
 *     aren't met. Callers can call methods on it safely (`?.capture()`
 *     pattern) without checking init state.
 *   - `initPostHog()` — explicit (async) initializer, called by the
 *     `PostHogProvider`. Idempotent; safe to call multiple times.
 *   - `resetPostHog()` — called on logout to flush identification.
 *   - `enterAnonymousMode()`: leave any identity an earlier visit persisted
 *     in this browser behind, and capture anonymously from then on.
 *     Idempotent. See "IDENTITY IS SETTLED BEFORE CAPTURE" below.
 *
 * NO server-side calls. Marketing-funnel events are fired from the
 * client. Auth-time identify is also client-side from the
 * post-login screen.
 *
 * Bundle note: `posthog-js` (~50-60KB gz) is loaded via dynamic
 * `import()` INSIDE `initPostHog()` — never statically — so the chunk
 * only downloads after the consent gate passes. The `PostHog` type
 * import below is type-only and erased at compile time.
 *
 * IDENTITY IS SETTLED BEFORE CAPTURE (RM-W1-10, Astra H3-01). posthog-js
 * reads the identity a browser persisted on an EARLIER visit (localStorage
 * plus cookie) and stamps it on every event until something changes it.
 * Before RM-W1-10 that identity was the email address, so a returning browser
 * would keep sending it long after the code stopped identifying by email; no
 * check around `identify()` can see that, because nothing calls `identify()`.
 * So this module keeps the loaded SDK away from every capture caller until the
 * persisted identity is one the current session permits. An "@" anywhere in it
 * is left behind the moment the SDK loads, whatever the session says. An
 * identified id is HELD until the session layer says which id, if any, is
 * right, through `identifyUser` or `enterAnonymousMode`. An anonymous id is
 * permitted in every mode and is published at once.
 *
 * Residual, recorded after the Fable review of 2026-09-12: the hold covers
 * CAPTURE only. posthog-js starts its remote-config and feature-flag fetch
 * right after `loaded`, with whatever id is persisted, so during the held
 * window a stale pseudonymous HMAC (a rotated key, or another account on
 * this browser) can reach the flags endpoint before the session decides.
 * It is never an email address and never an event.
 */

"use client";

import type { PostHog } from "posthog-js";
import { hasAnalyticsConsent } from "./consent";

/**
 * The loaded SDK: imported, initialised, persistence read. NOT something callers may capture
 * through; that is `instance`, published only once the identity is settled.
 */
let sdk: PostHog | null = null;
/**
 * The live instance callers capture through. Published by `settleIdentity` and by nothing else, so
 * the first event any caller can send already carries an identity the session permits.
 */
let instance: PostHog | null = null;
/** In-flight init, so concurrent callers share one import/init. */
let initPromise: Promise<boolean> | null = null;

/** What the session layer last asked the identity to be. `null` until it has said anything. */
type IdentityRequest =
  | { readonly kind: "anonymous" }
  | {
      readonly kind: "identified";
      readonly distinctId: string;
      readonly properties: Record<string, unknown> | undefined;
    };
let requestedIdentity: IdentityRequest | null = null;

/** Returns the live PostHog client if initialized + consented, else `null`. */
export function posthogClient(): PostHog | null {
  return instance;
}

/**
 * Initialize PostHog if env is set and consent granted. Idempotent:
 * subsequent calls after a successful init no-op. Resolves `true` when
 * the SDK is loaded after the call, `false` otherwise. Loaded is not the
 * same as capturable: `posthogClient()` stays `null` until the persisted
 * identity is settled (see `settleIdentity`). The consent + env
 * gates run BEFORE the dynamic import, so the posthog-js chunk never
 * downloads for non-consented sessions.
 */
export function initPostHog(): Promise<boolean> {
  if (sdk) return Promise.resolve(true);
  if (initPromise) return initPromise;
  if (typeof window === "undefined") return Promise.resolve(false);

  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  const host =
    process.env.NEXT_PUBLIC_POSTHOG_HOST ?? "https://us.i.posthog.com";
  if (!key) {
    // Missing env — silently no-op. Dev environments without keys
    // shouldn't spam console errors.
    return Promise.resolve(false);
  }
  if (!hasAnalyticsConsent()) {
    return Promise.resolve(false);
  }

  initPromise = (async () => {
    // Lazy-load the SDK only now that all gates have passed — this is
    // the whole first-load-JS win: posthog-js stays out of every
    // page's initial bundle.
    const { default: posthog } = await import("posthog-js");

    posthog.init(key, {
      api_host: host,
      // Capture pageviews manually via the Next.js router pattern in
      // the provider — auto-capture would double-fire on App Router
      // because of the way the History API is mocked.
      capture_pageview: false,
      // Don't auto-capture sensitive selectors — opt-in event design.
      autocapture: false,
      // Persistent ID via the cookie that PostHog manages itself.
      persistence: "localStorage+cookie",
      // The identity is settled HERE, and the placement is the fix (H3-01).
      // posthog-js calls `loaded` synchronously inside `init`, after it has
      // read the persisted identity and before it enables its request queue
      // or starts its remote-config and flags loader (lib/src/posthog-core.js,
      // `_init` then `_loaded`). Nothing has been captured yet and nothing has
      // left the browser. The default instance is closed over rather than
      // taken from the callback argument, which is typed as a narrower
      // interface; for the primary instance they are the same object.
      loaded: () => {
        adoptLoadedSdk(posthog);
      },
    });

    // Initialising an SDK that is already initialised is a no-op that does
    // not call `loaded` again (posthog-core.js `_init`, its `__loaded`
    // guard). Adopt it here in that case, so a fresh copy of this module
    // (fast refresh) still settles identity before publishing anything.
    if (!sdk) adoptLoadedSdk(posthog);
    return true;
  })().catch(() => {
    // Chunk-load failure (offline, ad-blocker) — clear the in-flight
    // marker so a later call can retry; analytics stays a silent no-op.
    initPromise = null;
    return false;
  });

  return initPromise;
}

/**
 * Whether a value may be sent to the analytics provider as a `distinct_id`
 * (RM-W1-10, ruling OR-04 variant A).
 *
 * This is a WALL, not a comment. It lives here, at the one place in the app that
 * speaks to the provider, so a future caller reaching for `session.user.email`
 * because it is to hand gets a silent no-op rather than a privacy regression
 * that only a reviewer would catch. An "@" is the one thing every address in the
 * user table has in common and is precisely the shape this rule exists to keep
 * out; nothing we legitimately send is an address, so the test is allowed to be
 * this blunt. Exported so it can be proved directly rather than inferred from
 * the absence of a call.
 */
export function isPermittedDistinctId(distinctId: string): boolean {
  if (typeof distinctId !== "string") return false;
  if (distinctId.length === 0) return false;
  return !distinctId.includes("@");
}

/**
 * posthog-js's persisted flag for whether the current distinct id names an identified person or an
 * anonymous one. Set to "anonymous" on a first visit and by `reset()`, and to "identified" by
 * `identify()` from an anonymous id (lib/src/constants.js `USER_STATE`; posthog-core.js `_init`,
 * `reset`, `identify`). A missing value counts as NOT anonymous here, so an unrecognised old
 * persistence blob is reset once rather than trusted.
 */
const USER_STATE_PROPERTY = "$user_state";
const USER_STATE_ANONYMOUS = "anonymous";

/**
 * The persisted super properties that carry identity onto EVERY event. posthog-js merges its whole
 * persistence into each event's properties (posthog-core.js `calculateEventProperties`) and none of
 * these three has a key policy hiding it (lib/src/persistence-key-policy.js), so an address in any
 * one of them leaves the browser on every capture: `distinct_id` directly, `$user_id` because
 * `identify()` registers it, and `$device_id` because `identify()` adopts the persisted distinct id
 * as the device id when an old blob stored none.
 */
const IDENTITY_PROPERTIES = ["distinct_id", "$user_id", "$device_id"] as const;

function carriesForbiddenIdentity(client: PostHog): boolean {
  return IDENTITY_PROPERTIES.some((name) => {
    const value: unknown = client.get_property(name);
    return typeof value === "string" && !isPermittedDistinctId(value);
  });
}

function isCleanAnonymous(client: PostHog): boolean {
  return (
    client.get_property(USER_STATE_PROPERTY) === USER_STATE_ANONYMOUS &&
    !carriesForbiddenIdentity(client)
  );
}

/**
 * Leave the persisted identity behind. `reset(true)`, not `reset()`: a plain reset mints a new
 * distinct id but KEEPS `$device_id` (posthog-core.js `reset`), and `$device_id` rides on every
 * event, so a plain reset would leave a join key from every new anonymous event back to the person
 * the old identity named. `reset` also clears the whole persistence blob, which is what removes
 * `$user_id`; re-registering `distinct_id` alone would not. `resetPostHog` (logout) keeps its plain
 * `reset()`: that is the unchanged logout behaviour, not the case this function exists for.
 */
function leavePersistedIdentity(client: PostHog): void {
  client.reset(true);
}

/**
 * Bring the loaded SDK's persisted identity into line with what the session layer asked for, then
 * publish it to capture callers. Idempotent by construction: every branch tests the persisted
 * state, never a counter, so a second call over a settled state finds nothing to reset, across
 * re-renders and across reloads alike.
 *
 *   no request yet   an "@" is left behind now, since no session permits it; a clean anonymous id
 *                    is published at once, since every session permits it; an identified id is
 *                    HELD, unpublished, until the session says which id is right
 *   anonymous        anything but a clean anonymous id is left behind
 *   identified as X  an id already equal to X is kept, so the correct HMAC is never reset; a
 *                    different IDENTIFIED id is left behind before identifying, so X is never
 *                    joined to it; a clean anonymous id is identified directly, which is the
 *                    designed anonymous-to-identified merge and carries no address
 */
function settleIdentity(client: PostHog): void {
  const request = requestedIdentity;
  if (request === null) {
    if (carriesForbiddenIdentity(client)) leavePersistedIdentity(client);
    if (isCleanAnonymous(client)) instance = client;
    return;
  }
  if (request.kind === "anonymous") {
    if (!isCleanAnonymous(client)) leavePersistedIdentity(client);
  } else {
    const alreadyThisId =
      client.get_distinct_id() === request.distinctId &&
      !carriesForbiddenIdentity(client);
    if (!alreadyThisId && !isCleanAnonymous(client)) {
      leavePersistedIdentity(client);
    }
    client.identify(request.distinctId, request.properties);
  }
  instance = client;
}

function adoptLoadedSdk(client: PostHog): void {
  sdk = client;
  settleIdentity(client);
}

/**
 * Enter anonymous mode: from now on this browser captures under an anonymous id, and any identity
 * an earlier visit persisted (an email from before RM-W1-10, or a pseudonymous id) is left behind
 * first. The provider calls it whenever the session carries no `analyticsId` (signed in with the
 * key unprovisioned, or signed out); any caller that must guarantee anonymity may call it too.
 *
 * Idempotent: it resets at most once for a given persisted identity, never on every render. Safe
 * before consent and before the SDK loads: it only records the request, which `loaded` applies
 * before the first capture. It never loads the SDK itself, so it cannot get round the consent gate.
 */
export function enterAnonymousMode(): void {
  requestedIdentity = { kind: "anonymous" };
  if (sdk) settleIdentity(sdk);
}

/**
 * Identify the current user in PostHog. Called on login. Safe to
 * call multiple times; PostHog handles re-identification gracefully.
 *
 * RM-W1-10 (ruling OR-04, variant A): the caller passes the SERVER-COMPUTED
 * pseudonymous analytics id (`session.user.analyticsId`, an HMAC-SHA256 of the
 * NextAuth user id under a server-side key), never an email address. Until that
 * ruling the `distinctId` WAS the raw email while the privacy notice promised
 * anonymised analytics; the notice and the code now agree, and the identifier is
 * opaque to the provider. The key and the name of the environment variable
 * holding it live in `src/lib/analytics/pseudonymous-id.ts`, which is server
 * only and is deliberately not named here: `tests/analytics-id-server-only.test.ts`
 * fails if that variable name appears in any module a client bundle can reach,
 * and a rule that blunt is one a stray comment cannot quietly widen.
 *
 * H3-01: the request is recorded even before the SDK loads and applied inside
 * `loaded`, before the first capture. A persisted identity that is a DIFFERENT
 * identified id (an email from before RM-W1-10, an HMAC under a rotated key, or
 * another account's HMAC) is left behind first, so this id is never joined to it.
 */
export function identifyUser(
  distinctId: string,
  properties?: Record<string, unknown>,
): void {
  if (!isPermittedDistinctId(distinctId)) return;
  requestedIdentity = { kind: "identified", distinctId, properties };
  if (sdk) settleIdentity(sdk);
}

/**
 * Update user properties on the current PostHog person. Used when
 * subscription state changes mid-session (e.g., post-Stripe webhook
 * the next page-load reflects the new tier).
 */
export function setUserProperties(properties: Record<string, unknown>): void {
  const client = posthogClient();
  if (!client) return;
  client.setPersonProperties(properties);
}

/**
 * Track an event. Safe wrapper that no-ops when PostHog isn't
 * initialized — callers don't need to gate on consent state.
 */
export function trackEvent(
  event: string,
  properties?: Record<string, unknown>,
): void {
  const client = posthogClient();
  if (!client) return;
  client.capture(event, properties);
}

/** Clear identification + opt out. Called on logout. */
export function resetPostHog(): void {
  const client = posthogClient();
  if (!client) return;
  client.reset();
}

/**
 * Called by the consent banner when the user grants consent AFTER
 * page load. Initializes PostHog and fires the initial pageview so
 * the user's first session is tracked from acceptance onward.
 */
export function onConsentGranted(): void {
  if (instance) return;
  void initPostHog().then((live) => {
    // Fire initial pageview at the post-consent moment (once the
    // lazily-loaded SDK is actually live).
    if (live) {
      posthogClient()?.capture("$pageview");
    }
  });
}
